import { LocationPicker, inputClass } from "@/components/LocationPicker";
import { hasNonLatin } from "@/lib/latinName";
import { GENDER_OPTIONS, type GenderValue } from "@/lib/gender";

export interface ProfileFieldsValue {
  firstName: string;
  lastName: string;
  gender: GenderValue;
  homeCity: string;
  homeCountry: string | null;
  phone: string;
}

/** Which mandatory fields are currently unsatisfied. The caller decides when to
 *  surface them (e.g. only after a submit attempt). */
export interface ProfileFieldsErrors {
  firstName?: boolean;
  lastName?: boolean;
  gender?: boolean;
}

interface Props {
  value: ProfileFieldsValue;
  onChange: (patch: Partial<ProfileFieldsValue>) => void;
  /** Hide the location row (e.g. a virtual event with no location to infer). */
  showLocation?: boolean;
  /** Inline "required" messages to show under each field, when set. */
  errors?: ProfileFieldsErrors;
}

const ERROR_RING = "border-destructive focus-visible:ring-destructive";
const fieldClass = (hasError?: boolean) =>
  `${inputClass} ${hasError ? ERROR_RING : ""}`;

/** The person info fields (gender, name, location, phone) shared by the Profile
 *  edit form and the inline registration gate, so both look and behave the same.
 *  Presentation only — validation state is passed in via `errors`. */
export function ProfileFields({
  value,
  onChange,
  showLocation = true,
  errors,
}: Props) {
  const nameNonLatin = hasNonLatin(value.firstName) || hasNonLatin(value.lastName);
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3">
        <div>
          <select
            aria-label="Gender"
            value={value.gender}
            onChange={(e) => onChange({ gender: e.target.value as GenderValue })}
            className={fieldClass(errors?.gender)}
          >
            {GENDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors?.gender ? (
            <p className="mt-1 text-xs text-destructive">Please select your gender.</p>
          ) : null}
        </div>
        <div>
          <input
            type="text"
            placeholder="First name *"
            aria-label="First name"
            value={value.firstName}
            onChange={(e) => onChange({ firstName: e.target.value })}
            required
            autoComplete="given-name"
            className={fieldClass(errors?.firstName)}
          />
          {errors?.firstName ? (
            <p className="mt-1 text-xs text-destructive">First name is required.</p>
          ) : null}
        </div>
        <div>
          <input
            type="text"
            placeholder="Last name *"
            aria-label="Last name"
            value={value.lastName}
            onChange={(e) => onChange({ lastName: e.target.value })}
            required
            autoComplete="family-name"
            className={fieldClass(errors?.lastName)}
          />
          {errors?.lastName ? (
            <p className="mt-1 text-xs text-destructive">Last name is required.</p>
          ) : null}
        </div>
        {nameNonLatin ? (
          <p className="text-xs text-destructive">
            Please enter your name in Latin letters — non-Latin names can't be
            saved.
          </p>
        ) : null}
      </div>
      {showLocation ? (
        <LocationPicker
          city={value.homeCity || null}
          country={value.homeCountry}
          onChange={({ city, country }) =>
            onChange({ homeCity: city ?? "", homeCountry: country })
          }
        />
      ) : null}
      <div>
        <input
          type="tel"
          placeholder="Phone (optional)"
          aria-label="Phone"
          value={value.phone}
          onChange={(e) => onChange({ phone: e.target.value })}
          autoComplete="tel"
          className={inputClass}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          For safety on track purposes. We won't contact you — no communication
          is expected at this stage.
        </p>
      </div>
    </div>
  );
}
